import React, { useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import queryStaleTimes from '@/api/queryStaleTimes';
import { InputText, InputTextarea, ReactSelect } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useGetBrands } from '@/generated/brands';
import { useGetCustomerSegments } from '@/generated/customer-segment';
import {
	RegistrationCompositeDetailsResponse,
	CustomerApplicationResponse,
	CustomerSegmentId,
	CustomerSegmentResponse,
	GlobalListType,
} from '@/generated/data.schemas';
import { useGetTranslations } from '@/generated/translations';
import styles from '../ApplicationDetail.module.scss';
import { GlobalListDropdown } from './GlobalListDropdown';
import { LinkedTemperatures } from './LinkedTemperatures';
import type { Option } from './types';

interface ApplicationFormProps {
	formId: string;
	legend?: string;
	application?: RegistrationCompositeDetailsResponse;
	validationErrors: Record<string, string[]> | null;
	isOpen: boolean;
	handleSubmit: (e) => void;
	handleChange: () => void;
	isFullHeight?: boolean;
}

const OtherOption = {
	label: 'Other',
	value: 'Other',
};

export const ApplicationForm: React.FunctionComponent<ApplicationFormProps> = ({
	formId,
	legend,
	application,
	validationErrors,
	isOpen,
	handleSubmit,
	handleChange,
	isFullHeight,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);

	const [segmentSystemName, setSegmentSystemName] = useState<CustomerSegmentId | undefined>(
		application?.segmentSystemName || undefined,
	);

	const [currentBrand, setCurrentBrand] = useState<string | null>(application?.brand || null);

	const [applicationSystemName, setApplicationSystemName] = useState<CustomerSegmentId | null>(
		application?.applicationSystemName || null,
	);

	const [showOtherApplicationInput, setShowOtherApplicationInput] = useState(
		application?.applicationSystemName && application?.applicationSystemName.toLowerCase() === 'other',
	);

	const [showOtherApplicationBrandInput, setShowOtherApplicationBrandInput] = useState(
		application?.brand && application?.brand.toLowerCase() === 'other',
	);

	const { data: segments, isLoading: isSegmentLoading } = useGetCustomerSegments(segmentationId, {
		query: { enabled: isOpen, staleTime: queryStaleTimes.customerSegments },
	});

	/* Application Brand - different from Fluid Brand! */
	const { data: brands } = useGetBrands(segmentationId, {
		Segment: segmentSystemName,
	});

	const applications = useMemo(
		() => segments?.data.find((application) => application.id === segmentSystemName)?.applications,
		[segments, segmentSystemName],
	);

	const mappedSegmentsOptions = useMemo((): Option[] | undefined => {
		const options = segments?.data.map(({ name, id }: CustomerSegmentResponse) => ({
			label: name,
			value: id,
		}));

		return options
			? [{ label: translations?.data.applicationRegistrations.chooseSegment || '', value: '' }, ...options]
			: undefined;
	}, [segments, translations]);

	const mappedApplicationOptions = useMemo((): Option[] => {
		const options = applications?.map(({ name, id }: CustomerApplicationResponse) => ({
			label: name,
			value: id,
		}));

		return options
			? [
					{ label: translations?.data.applicationRegistrations.chooseApplication || '', value: '' },
					...options,
					OtherOption,
				]
			: [{ label: translations?.data.applicationRegistrations.chooseSegmentFirst || '', value: '' }];
	}, [translations, applications]);

	const brandsOptions = useMemo(() => {
		if (!brands?.data) return [OtherOption];
		const options = brands
			? brands?.data.brands.map((brand) => ({
					label: brand.name,
					value: brand.id,
				}))
			: [];
		return [...options, OtherOption];
	}, [brands]);

	const selectedBrand = useMemo(() => {
		return currentBrand ? (brandsOptions?.find((x) => x.value === currentBrand) ?? OtherOption) : undefined;
	}, [brandsOptions, currentBrand]);

	const selectedApplication = useMemo(() => {
		return applicationSystemName
			? mappedApplicationOptions?.find((x) => x.value === applicationSystemName) || OtherOption
			: undefined;
	}, [mappedApplicationOptions, applicationSystemName]);

	const handleSegmentChange = (option: Option): void => {
		setSegmentSystemName(option.value);
		setCurrentBrand(null);
		setApplicationSystemName(null);
		handleChange();
	};

	const handleApplicationChange = (option: Option): void => {
		if (applications && option.value !== '') {
			setApplicationSystemName(option.value.toLowerCase() === 'other' ? '' : option.value);
			setShowOtherApplicationInput(option.value.toLowerCase() === 'other');
			handleChange();
		}
	};

	const modifyApplication = (e) => {
		setApplicationSystemName(e.target.value);
		handleChange();
	};

	const handleBrandChange = (option: Option): void => {
		setCurrentBrand(option.value.toLowerCase() === 'other' ? '' : option.value);
		setShowOtherApplicationBrandInput(option.value.toLowerCase() === 'other');
		handleChange();
	};

	const modifyApplicationBrand = (e) => {
		setCurrentBrand(e.target.value);
		handleChange();
	};

	useEffect(() => {
		if (applications && application?.applicationSystemName) {
			setShowOtherApplicationInput(
				applications.findIndex((x) => x.id === application?.applicationSystemName) === -1,
			);
		}

		if (brands && application?.brand) {
			setShowOtherApplicationBrandInput(
				brands.data.brands.findIndex((x) => x.id === application.applicationSystemName) === -1,
			);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [brands, applications]);

	return (
		<form
			id={formId}
			onSubmit={handleSubmit}
			className={classNames(styles.form, { [styles.isFullHeight]: isFullHeight })}
		>
			{/* Segment, Application, Brand, Model */}
			<fieldset className={styles.fieldset}>
				<legend className={'u-hide-visually'}>{legend}</legend>
				<InputText
					name="name"
					id="name"
					defaultValue={application?.name ?? ''} // if name is null, we want the input to be empty, not to show 'null'
					label={translations?.data.applications.nameOfApplication}
					errorText={validationErrors?.Name?.[0]}
					onChange={handleChange}
					variant={'inverse'}
					isSmall
				/>
			</fieldset>
			{!isSegmentLoading && mappedSegmentsOptions && (
				<>
					<ReactSelect
						ariaLabel={translations?.data.applications.segment || ''}
						id="segment"
						name="segment"
						options={mappedSegmentsOptions}
						currentValue={mappedSegmentsOptions.find((x) => x.value === segmentSystemName)}
						errorText={validationErrors?.SegmentSystemName?.[0]}
						onChange={handleSegmentChange}
						variant={'inverse'}
						wrapperClassName={styles.selectWrapper}
						required
						isSmall
					/>
					<fieldset className={styles.fieldset}>
						<legend className={classNames(styles.legend, styles.required)}>
							{translations?.data.applications.application}
						</legend>
						<div className={styles.flex}>
							<ReactSelect
								ariaLabel={translations?.data.applications.application || ''}
								id="application"
								options={mappedApplicationOptions}
								currentValue={selectedApplication}
								errorText={validationErrors?.ApplicationSystemName?.[0]}
								onChange={handleApplicationChange}
								variant={'inverse'}
								required
								hideLabel
								isSmall
							/>
							<InputText
								name="application"
								id="application"
								value={applicationSystemName || ''}
								label={translations?.data.applications.application}
								onChange={modifyApplication}
								variant={'inverse'}
								isHidden={!showOtherApplicationInput}
								className={styles.linkedInput}
								hideLabel
								disabled={!applications}
								isSmall
							/>
						</div>
					</fieldset>
				</>
			)}
			<fieldset className={styles.fieldset}>
				<legend className={styles.legend}>{translations?.data.applications.brand}</legend>
				<div className={styles.flex}>
					<ReactSelect
						id="brand"
						options={brandsOptions}
						currentValue={selectedBrand}
						ariaLabel={translations?.data.applications.brand || 'Brand'}
						onChange={handleBrandChange}
						variant={'inverse'}
						hideLabel
						isSmall
					/>
					<InputText
						name="brand"
						id="brand"
						value={currentBrand || ''}
						label={translations?.data.applications.brand}
						onChange={modifyApplicationBrand}
						variant={'inverse'}
						isHidden={!showOtherApplicationBrandInput}
						className={styles.linkedInput}
						hideLabel
						isSmall
					/>
				</div>
			</fieldset>
			{/* Model */}
			<div className={styles.fieldset}>
				<InputText
					name="model"
					id="model"
					defaultValue={application?.model || ''}
					label={translations?.data.applications.model}
					onChange={handleChange}
					variant={'inverse'}
					isSmall
				/>
			</div>

			{/* Oil System */}
			<GlobalListDropdown
				id={'oilSystem'}
				handleChange={handleChange}
				type={GlobalListType.OilSystems}
				initialValue={application?.oilSystem}
				segment={segmentSystemName}
			/>

			{/* Oil System Brand */}
			<GlobalListDropdown
				id={'oilSystemBrand'}
				handleChange={handleChange}
				type={GlobalListType.OilSystemBrands}
				initialValue={application?.oilSystemBrand}
				segment={segmentSystemName}
			/>
			{/* Fluid Brand */}
			<GlobalListDropdown
				id={'fluidBrand'}
				handleChange={handleChange}
				type={GlobalListType.OilDieselFluid}
				initialValue={application?.fluidBrand}
			/>
			{/* Fluid Type */}
			<InputText
				name="fluidType"
				id="fluidType"
				defaultValue={application?.fluidType || ''}
				label={translations?.data.applications.fluidType || 'Fluid Type'}
				onChange={handleChange}
				variant={'inverse'}
				isSmall
			/>
			<InputText
				name="viscosity"
				id="viscosity"
				defaultValue={application?.viscosity || ''}
				label={translations?.data.applications.viscosity}
				onChange={handleChange}
				variant={'inverse'}
				type="number"
				suffix={translations?.data.applications.viscosityUnit}
				isSmall
			/>
			<InputText
				name="systemVolume"
				id="systemVolume"
				defaultValue={application?.systemVolume || ''}
				label={translations?.data.applications.systemVolume}
				onChange={handleChange}
				variant={'inverse'}
				type="number"
				suffix={translations?.data.applications.volumeUnit}
				isSmall
			/>
			{/* Ambient Temperature */}
			<fieldset className={styles.fieldset}>
				<legend className={styles.legend}>{translations?.data.applications.ambientTemperature}</legend>
				<LinkedTemperatures
					onChange={handleChange}
					id={'AmbientTemperature'}
					defaultMin={application?.minAmbientTemperature}
					defaultMax={application?.maxAmbientTemperature}
				/>
			</fieldset>
			{/* Operation Temperature */}
			<fieldset className={styles.fieldset}>
				<legend className={styles.legend}>{translations?.data.applications.operationTemperature}</legend>
				<LinkedTemperatures
					onChange={handleChange}
					id={'OperationTemperature'}
					defaultMin={application?.minOperationTemperature}
					defaultMax={application?.maxOperationTemperature}
					defaultNorm={application?.normalOperationTemperature}
					hasNorm
				/>
			</fieldset>
			{/* Power Supply */}
			<GlobalListDropdown
				id={'powerSupply'}
				handleChange={handleChange}
				type={GlobalListType.PowerSupply}
				initialValue={application?.powerSupply}
			/>
			{/* Additional Information */}
			<fieldset className={styles.fieldset}>
				<InputTextarea
					name="additionalInformation"
					id="additionalInformation"
					defaultValue={application?.additionalInformation || ''}
					label={translations?.data.applications.additionalInformation}
					onChange={handleChange}
					variant={'inverse'}
					className={styles.textarea}
				/>
			</fieldset>
		</form>
	);
};
